import { Form, Input, Radio, message } from "antd";
import { PlusCircleFilled } from "@ant-design/icons";
import { capitalize } from "lodash";
import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-number-input";
import { toTitleCase } from "../../utils";
import AdditionalEmailForm from "./common/AdditionalEmailForm";
import AdditionalPhoneForm from "./common/AdditionalPhoneForm";
import "./leadCenter.css";

// AdditionalEmailForm and AdditionalPhoneForm are now imported from common folder

// A contact belongs to the lead themselves when it is tagged "Self" or untagged
// (CRM rows often arrive without relationshipType). Anything else (Spouse, etc.)
// lives in the Additional section and must never bind to the Personal fields.
const isSelfContact = item => !item?.relationshipType || item.relationshipType === "Self";

/**
 *
 * @param {{form:import("antd").FormInstance}} param0
 * @returns
 */

export default function LeadCustomerInformationForm({
  leadData,
  setleadData,
  primaryPhoneType,
  setprimaryPhoneType,
  phoneArr,
  primaryEmailType,
  setprimaryEmailType,
  emailTypes,
  form,
  mode,
}) {
  const [addtionalEmails, setAdditionalEmails] = useState([]);
  const [addtionalPhones, setAdditionalPhones] = useState([]);

  // Reusable function to clear validation errors for empty fields
  const clearValidationErrorsForEmptyFields = fieldNames => {
    const currentValues = form.getFieldsValue(fieldNames);

    fieldNames.forEach(fieldName => {
      const fieldValue = currentValues[fieldName];
      if (!fieldValue || fieldValue.trim() === "") {
        form.setFields([
          {
            name: fieldName,
            errors: [],
          },
        ]);
      }
    });
  };

  const handleChangePhone = (e, key) => {
    // Clear email field validation errors only if email fields are empty
    const emailFieldNames = emailTypes.map(type => `${type}_EMAIL`);
    clearValidationErrorsForEmptyFields(emailFieldNames);

    setleadData(prev => {
      const temp = structuredClone(prev);
      const index = temp.phones.findIndex(
        item => toTitleCase(item.phoneType) === key && isSelfContact(item)
      );
      if (index !== -1) {
        temp.phones[index] = {
          ...temp.phones[index],
          phone: e,
          phoneType: key,
          relationshipType: "Self",
          id: temp.phones[index].id,
          firstName: temp.phones[index]?.firstName || leadData.firstName,
          lastName: temp.phones[index]?.lastName || leadData.lastName,
        };
      } else {
        temp.phones.push({
          phone: e,
          phoneType: key,
          relationshipType: "Self",
          firstName: temp.phones[index]?.firstName || leadData.firstName,
          lastName: temp.phones[index]?.lastName || leadData.lastName,
        });
      }
      return temp;
    });
  };

  const getPhoneValue = key => {
    return leadData.phones.find(
      item =>
        toTitleCase(item.phoneType) === key &&
        (!item.relationshipType || item.relationshipType === "Self")
    )?.phone;
  };

  useEffect(() => {
    if (primaryPhoneType?.startsWith?.("additional_")) return;
    if (!leadData.phones || leadData.phones.length === 0) return;

    const primaryPhone = leadData.phones.find(item => item.isPrimary);
    if (!primaryPhone) return;

    // Walk in the same order used by the additional-phones init so we get the
    // correct "additional_<index>" if the primary is in the additional bucket.
    const seenSelfTypes = new Set();
    let additionalCounter = 0;
    let primaryAdditionalIndex = -1;
    let primaryIsSelf = false;

    leadData.phones.forEach(phone => {
      const isSelfTagged = !phone.relationshipType || phone.relationshipType === "Self";
      const typeKey = toTitleCase(phone.phoneType);
      if (isSelfTagged && !seenSelfTypes.has(typeKey)) {
        seenSelfTypes.add(typeKey);
        if (phone === primaryPhone) primaryIsSelf = true;
      } else {
        if (phone === primaryPhone) primaryAdditionalIndex = additionalCounter;
        additionalCounter++;
      }
    });

    if (primaryIsSelf) {
      setprimaryPhoneType(toTitleCase(primaryPhone.phoneType));
    } else if (primaryAdditionalIndex !== -1) {
      setprimaryPhoneType(`additional_${primaryAdditionalIndex}`);
    }
  }, [leadData.phones]);

  useEffect(() => {
    if (primaryEmailType?.startsWith?.("additional_")) return;
    if (!leadData.emails || leadData.emails.length === 0) return;

    const primaryEmail = leadData.emails.find(item => item.isPrimary);
    if (!primaryEmail) return;

    const seenSelfTypes = new Set();
    let additionalCounter = 0;
    let primaryAdditionalIndex = -1;
    let primaryIsSelf = false;

    leadData.emails.forEach(email => {
      const isSelfTagged = !email.relationshipType || email.relationshipType === "Self";
      if (isSelfTagged && !seenSelfTypes.has(email.emailType)) {
        seenSelfTypes.add(email.emailType);
        if (email === primaryEmail) primaryIsSelf = true;
      } else {
        if (email === primaryEmail) primaryAdditionalIndex = additionalCounter;
        additionalCounter++;
      }
    });

    if (primaryIsSelf) {
      setprimaryEmailType(primaryEmail.emailType);
    } else if (primaryAdditionalIndex !== -1) {
      setprimaryEmailType(`additional_${primaryAdditionalIndex}`);
    }
  }, [leadData.emails]);

  // Populate additional emails and phones when editing - run only once on mount
  const [isEmailsInitialized, setIsEmailsInitialized] = React.useState(false);
  const [isPhonesInitialized, setIsPhonesInitialized] = React.useState(false);

  useEffect(() => {
    if (mode === "EDIT" && !isEmailsInitialized && leadData.emails && leadData.emails.length > 0) {
      // An email goes to "Additional" if it's explicitly non-Self, OR if its emailType
      // already has a Self-tagged (or untagged) entry — i.e. it's a duplicate.
      const seenSelfTypes = new Set();
      const additionalEmailsData = leadData.emails.filter(email => {
        const isSelfTagged = !email.relationshipType || email.relationshipType === "Self";
        if (isSelfTagged && !seenSelfTypes.has(email.emailType)) {
          seenSelfTypes.add(email.emailType);
          return false;
        }
        return true;
      });
      if (additionalEmailsData.length > 0) {
        const formattedAdditionalEmails = additionalEmailsData.map(email => ({
          id: email.id || Math.random(),
          email: email.email,
          emailType: email.emailType,
          relation: email.relationshipType,
          firstName: email.firstName || "",
          lastName: email.lastName || "",
          note: email.note || "",
          isPrimary: false,
          isDeleted: false,
        }));

        setAdditionalEmails(formattedAdditionalEmails);

        // Set form values for additional emails
        const additionalEmailFormValues = {};
        formattedAdditionalEmails.forEach((email, index) => {
          additionalEmailFormValues[`additional_email_${index}`] = email.email;
          additionalEmailFormValues[`additional_email_type_${index}`] = email.emailType;
          additionalEmailFormValues[`additional_email_relation_${index}`] = email.relation;
          additionalEmailFormValues[`additional_email_firstName_${index}`] = email.firstName;
          additionalEmailFormValues[`additional_email_lastName_${index}`] = email.lastName;
          additionalEmailFormValues[`additional_email_note_${index}`] = email.note;
        });
        form.setFieldsValue(additionalEmailFormValues);
      }
      setIsEmailsInitialized(true);
    }
  }, [mode, leadData.emails, isEmailsInitialized]);

  useEffect(() => {
    if (mode === "EDIT" && !isPhonesInitialized && leadData.phones && leadData.phones.length > 0) {
      // A phone goes to "Additional" if it's explicitly non-Self, OR if its phoneType
      // already has a Self-tagged (or untagged) entry — i.e. it's a duplicate.
      const seenSelfTypes = new Set();
      const additionalPhonesData = leadData.phones.filter(phone => {
        const isSelfTagged = !phone.relationshipType || phone.relationshipType === "Self";
        const phoneTypeKey = toTitleCase(phone.phoneType);
        if (isSelfTagged && !seenSelfTypes.has(phoneTypeKey)) {
          seenSelfTypes.add(phoneTypeKey);
          return false;
        }
        return true;
      });

      if (additionalPhonesData.length > 0) {
        const formattedAdditionalPhones = additionalPhonesData.map(phone => ({
          id: phone.id || Math.random(),
          phone: phone.phone,
          phoneType: phone.phoneType,
          relation: phone.relationshipType,
          firstName: phone.firstName || "",
          lastName: phone.lastName || "",
          note: phone.note || "",
          isPrimary: false,
          isDeleted: false,
        }));

        setAdditionalPhones(formattedAdditionalPhones);

        // Set form values for additional phones
        const additionalPhoneFormValues = {};
        formattedAdditionalPhones.forEach((phone, index) => {
          additionalPhoneFormValues[`additional_phone_${index}`] = phone.phone;
          additionalPhoneFormValues[`additional_phone_type_${index}`] = phone.phoneType;
          additionalPhoneFormValues[`additional_phone_relation_${index}`] = phone.relation;
          additionalPhoneFormValues[`additional_phone_firstName_${index}`] = phone.firstName;
          additionalPhoneFormValues[`additional_phone_lastName_${index}`] = phone.lastName;
          additionalPhoneFormValues[`additional_phone_note_${index}`] = phone.note;
        });
        form.setFieldsValue(additionalPhoneFormValues);
      }
      setIsPhonesInitialized(true);
    }
  }, [mode, leadData.phones, isPhonesInitialized]);

  const resetEmailFields = () => {
    // Extract only email-related field names
    const emailFieldNames = emailTypes.map(type => `${type}_EMAIL`);
    form.resetFields(emailFieldNames); // Reset only email fields
  };
  useEffect(() => {
    if (leadData.emails && leadData.emails.length > 0) {
      const emailValues = emailTypes.reduce((acc, type) => {
        const emailTypeData = leadData.emails.find(
          el => el?.emailType === type && isSelfContact(el)
        );
        acc[`${type}_EMAIL`] = emailTypeData ? emailTypeData?.email : "";
        return acc;
      }, {});

      // Only update form values if they are different from current form values
      const currentValues = form.getFieldsValue(Object.keys(emailValues));
      const hasChanges = Object.keys(emailValues).some(
        key => currentValues[key] !== emailValues[key]
      );
      if (hasChanges) {
        form.setFieldsValue(emailValues);
      }
    } else {
      resetEmailFields();
    }
  }, [leadData.emails]);

  const setEmail = (value, type) => {
    // Clear phone field validation errors only if phone fields are empty
    clearValidationErrorsForEmptyFields(phoneArr);

    setleadData(prev => {
      const temp = structuredClone(prev);

      const index = temp.emails.findIndex(
        item => toTitleCase(item.emailType) === toTitleCase(type) && isSelfContact(item)
      );
      if (index !== -1) {
        temp.emails[index] = {
          ...temp.emails[index],
          email: value,
          emailType: type,
          relationshipType: "Self",
          id: temp.emails[index].id,
          firstName: temp.emails[index]?.firstName || leadData.firstName,
          lastName: temp.emails[index]?.lastName || leadData.lastName,
        };
      } else {
        temp.emails.push({
          email: value,
          emailType: type,
          relationshipType: "Self",
          firstName: temp.emails[index]?.firstName || leadData.firstName,
          lastName: temp.emails[index]?.lastName || leadData.lastName,
        });
      }
      return temp;
    });
  };

  const hasValidEmail = () =>
    leadData.emails?.some(email => email.email && email.email.trim() !== "");
  const hasValidPhone = () =>
    leadData.phones?.some(phone => phone.phone && phone.phone.trim() !== "");

  // Function to collect all additional emails from form
  const collectAdditionalEmails = () => {
    console.log("Collecting additional emails...", addtionalEmails);
    const formValues = form.getFieldsValue();
    const additionalEmailsData = [];

    addtionalEmails.forEach((emailItem, index) => {
      if (emailItem.isDeleted) {
        // For deleted items, only send ID if it's an existing database record
        if (emailItem.id && !emailItem.id.toString().startsWith("0.")) {
          additionalEmailsData.push({
            id: emailItem.id,
          });
          console.log("✅ DELETE: Email", emailItem.id);
        } else {
          console.log("❌ SKIP DELETE: Not valid DB ID", emailItem.id);
        }
      } else {
        // For non-deleted items, get current form values
        const email = formValues[`additional_email_${index}`];
        const emailType = formValues[`additional_email_type_${index}`];
        const relation = formValues[`additional_email_relation_${index}`];
        const firstName = formValues[`additional_email_firstName_${index}`];
        const lastName = formValues[`additional_email_lastName_${index}`];
        const note = formValues[`additional_email_note_${index}`];

        if (email) {
          const emailData = {
            email,
            emailType,
            relationshipType: relation || "Other",
            firstName,
            lastName,
            note,
            isPrimary: primaryEmailType === `additional_${index}`,
          };

          // Include id for existing records (update), omit for new records (create)
          if (emailItem.id && !emailItem.id.toString().startsWith("0.")) {
            emailData.id = emailItem.id;
            console.log("✅ UPDATE: Email", emailItem.id);
          } else {
            console.log("✅ CREATE: New email");
          }
          additionalEmailsData.push(emailData);
        }
      }
    });

    console.log("Final emails payload:", additionalEmailsData);
    return additionalEmailsData;
  };

  // Function to collect all additional phones from form
  const collectAdditionalPhones = () => {
    const formValues = form.getFieldsValue();
    const additionalPhonesData = [];

    addtionalPhones.forEach((phoneItem, index) => {
      if (phoneItem.isDeleted) {
        // For deleted items, only send ID if it's an existing database record
        if (phoneItem.id && !phoneItem.id.toString().startsWith("0.")) {
          additionalPhonesData.push({
            id: phoneItem.id,
          });
          console.log("✅ DELETE: Phone", phoneItem.id);
        } else {
          console.log("❌ SKIP DELETE: Not valid DB ID", phoneItem.id);
        }
      } else {
        // For non-deleted items, get current form values
        const phone = formValues[`additional_phone_${index}`];
        const phoneType = formValues[`additional_phone_type_${index}`];
        const relation = formValues[`additional_phone_relation_${index}`];
        const firstName = formValues[`additional_phone_firstName_${index}`];
        const lastName = formValues[`additional_phone_lastName_${index}`];
        const note = formValues[`additional_phone_note_${index}`];

        if (phone) {
          const phoneData = {
            phone,
            phoneType,
            relationshipType: relation || "Other",
            firstName,
            lastName,
            note,
            isPrimary: primaryPhoneType === `additional_${index}`,
          };

          // Include id for existing records (update), omit for new records (create)
          if (phoneItem.id && !phoneItem.id.toString().startsWith("0.")) {
            phoneData.id = phoneItem.id;
            console.log("✅ UPDATE: Phone", phoneItem.id);
          } else {
            console.log("✅ CREATE: New phone");
          }
          additionalPhonesData.push(phoneData);
        }
      }
    });

    return additionalPhonesData;
  };

  // Expose collection functions via form instance
  React.useEffect(() => {
    if (form) {
      form.collectAdditionalEmails = collectAdditionalEmails;
      form.collectAdditionalPhones = collectAdditionalPhones;
    }
  }, [addtionalEmails, addtionalPhones, primaryPhoneType, primaryEmailType]);

  return (
    <div className="flex flex-wrap ">
      <span className="bg-[#F8FAFC] p-2 w-full text-sm font-[600] text-grey-700 ">
        Personal Details
      </span>
      <div className="flex justify-between w-full flex-wrap">
        <Form.Item
          className="w-[48%]"
          name="firstName"
          rules={[{ required: true, message: "First name is required" }]}
          label="First Name"
        >
          <Input
            name="firstName"
            value={leadData.firstName}
            placeholder="Enter first name"
            onChange={e => setleadData({ ...leadData, firstName: e.target.value })}
          />
        </Form.Item>
        <Form.Item
          className="w-[48%]"
          name="lastName"
          rules={[{ required: true, message: "Last name is required" }]}
          label="Last Name"
        >
          <Input
            name="lastName"
            placeholder="Enter last name"
            value={leadData.lastName}
            onChange={e => setleadData({ ...leadData, lastName: e.target.value })}
          />
        </Form.Item>

        <Radio.Group
          value={primaryEmailType}
          onChange={e => {
            const selectedValue = e.target.value;

            if (emailTypes.includes(selectedValue)) {
              const emailValue = leadData.emails.find(
                email => email.emailType === selectedValue && isSelfContact(email)
              )?.email;

              if (!emailValue || emailValue.trim() === "") {
                message.warning(
                  `Please enter a ${capitalize(selectedValue)} email before marking it as primary`
                );
                return;
              }
            } else if (selectedValue.startsWith("additional_")) {
              const index = parseInt(selectedValue.replace("additional_", ""), 10);
              const emailValue = form.getFieldValue(`additional_email_${index}`);
              if (!emailValue || emailValue.trim() === "") {
                message.warning("Please enter an email before marking it as primary");
                return;
              }
            }

            setprimaryEmailType(selectedValue);
          }}
          className="flex-col-centered items-center w-full"
        >
          {emailTypes.map((item, index) => (
            <div key={index} className="flex-row-centered gap-2 justify-between w-full">
              <Form.Item
                rules={[
                  {
                    required: primaryEmailType === item && !hasValidPhone(),
                    message: `Please enter the ${capitalize(item)} email`,
                  },
                  { type: "email", message: `Enter a valid ${capitalize(item)} email` },
                ]}
                label={`Email (${capitalize(item)})`}
                name={`${item}_EMAIL`}
                className="w-[95%]"
                key={`${item}_EMAIL`}
              >
                <Input
                  className="w-full"
                  onChange={e => setEmail(e.target.value, item)}
                  name={`${item}_EMAIL`}
                  placeholder={`Enter ${capitalize(item)} email`}
                />
              </Form.Item>
              <Radio className="mt-4" value={item} />
            </div>
          ))}

          {addtionalEmails.length > 0 &&
            addtionalEmails.map((emailItem, originalIndex) => {
              if (emailItem.isDeleted) return null;
              return (
                <AdditionalEmailForm
                  key={`email_key__${emailItem.id}`}
                  item={emailItem}
                  index={originalIndex}
                  showPrimary
                  primaryValue={`additional_${originalIndex}`}
                  isPrimary={primaryEmailType === `additional_${originalIndex}`}
                  onDelete={() => {
                    if (primaryEmailType === `additional_${originalIndex}`) {
                      message.error(
                        "This email is marked as primary. Please mark another email as primary before deleting."
                      );
                      return;
                    }
                    setAdditionalEmails(prev => {
                      return prev.map(item =>
                        item.id === emailItem.id ? { ...item, isDeleted: true } : item
                      );
                    });
                  }}
                />
              );
            })}
        </Radio.Group>
        <span
          className="flex-row-centered w-full ml-1 mb-1 mt-1 text-brand-500 items-center gap-1 cursor-pointer"
          onClick={() => {
            setAdditionalEmails(prev => {
              const newState = [...prev];
              newState.push({
                id: Math.random(),
                email: "",
                emailType: "",
                relation: "",
                firstName: "",
                lastName: "",
                note: "",
                isPrimary: false,
                isDeleted: false,
              });
              return newState;
            });
          }}
        >
          <PlusCircleFilled /> Additional Email
        </span>

        <Radio.Group
          value={primaryPhoneType}
          onChange={e => {
            const selectedValue = e.target.value;

            if (phoneArr.includes(selectedValue)) {
              const phoneValue = leadData.phones.find(
                phone =>
                  toTitleCase(phone.phoneType) === selectedValue &&
                  (!phone.relationshipType || phone.relationshipType === "Self")
              )?.phone;

              if (!phoneValue || phoneValue.trim() === "") {
                message.warning(
                  `Please enter a ${selectedValue} phone number before marking it as primary`
                );
                return;
              }
            } else if (selectedValue.startsWith("additional_")) {
              const index = parseInt(selectedValue.replace("additional_", ""), 10);
              const phoneValue = form.getFieldValue(`additional_phone_${index}`);
              if (!phoneValue || phoneValue.trim() === "") {
                message.warning("Please enter a phone number before marking it as primary");
                return;
              }
            }

            setprimaryPhoneType(selectedValue);
          }}
          className="flex-col-centered items-center w-full"
        >
          {phoneArr.map((item, index) => (
            <div key={index} className="flex-row-centered gap-2 justify-between w-full">
              <Form.Item
                key={index}
                className="w-full"
                name={item}
                rules={[
                  {
                    required: item === primaryPhoneType && !hasValidEmail(),
                    message: `${item} Phone number is required`,
                  },
                ]}
                label={`Phone (${item})`}
              >
                <div className="flex-row-centered items-center justify-between gap-2 w-full">
                  <PhoneInput
                    value={getPhoneValue(item)}
                    onChange={e => handleChangePhone(e, item)}
                    className="aboutThisLead w-full "
                    placeholder={`Enter ${item} number`}
                    name={item}
                  />
                </div>
              </Form.Item>
              <Radio value={item} className="mt-4" />
            </div>
          ))}

          {addtionalPhones.length > 0 &&
            addtionalPhones.map((phoneItem, originalIndex) => {
              if (phoneItem.isDeleted) return null;
              return (
                <AdditionalPhoneForm
                  key={`phone_key__${phoneItem.id}`}
                  item={phoneItem}
                  index={originalIndex}
                  showPrimary
                  primaryValue={`additional_${originalIndex}`}
                  isPrimary={primaryPhoneType === `additional_${originalIndex}`}
                  onDelete={() => {
                    if (primaryPhoneType === `additional_${originalIndex}`) {
                      message.error(
                        "This phone is marked as primary. Please mark another phone as primary before deleting."
                      );
                      return;
                    }
                    setAdditionalPhones(prev => {
                      return prev.map(item =>
                        item.id === phoneItem.id ? { ...item, isDeleted: true } : item
                      );
                    });
                  }}
                />
              );
            })}
        </Radio.Group>
        <span
          className="flex-row-centered ml-1 cursor-pointer text-brand-500 items-center gap-1"
          onClick={() => {
            setAdditionalPhones(prev => {
              const newState = [...prev];
              newState.push({
                id: Math.random(),
                phone: "",
                phoneType: "",
                relation: "",
                firstName: "",
                lastName: "",
                note: "",
                isPrimary: false,
                isDeleted: false,
              });
              return newState;
            });
          }}
        >
          <PlusCircleFilled /> Additional Phone
        </span>
        <span className="bg-[#F8FAFC] p-2 w-full text-sm font-[600] text-grey-700 ">
          Communication Details
        </span>
        <Form.Item
          className="w-full"
          name="addressLine1"
          // rules={[{ required: true, message: "Primary Address is required" }]}
          label="Address Line 1"
        >
          <Input
            name="addressLine1"
            placeholder="Enter Address Line 1"
            value={leadData.addressLine1}
            onChange={e => setleadData({ ...leadData, addressLine1: e.target.value })}
          />
        </Form.Item>
        <Form.Item className="w-full" name="addressLine2" label="Address Line 2">
          <Input
            name="addressLine2"
            placeholder="Enter Address Line 2"
            value={leadData.addressLine2}
            onChange={e => setleadData({ ...leadData, addressLine2: e.target.value })}
          />
        </Form.Item>
        <Form.Item
          className="w-full"
          name="city"
          // rules={[{ required: true, message: "City is required" }]}
          label="City"
        >
          <Input
            name="city"
            placeholder="Enter City"
            value={leadData.city}
            onChange={e => setleadData({ ...leadData, city: e.target.value })}
          />
        </Form.Item>
        <Form.Item
          className="w-[48%]"
          name="state"
          // rules={[{ required: true, message: "State is required" }]}
          label="State"
        >
          <Input
            name="state"
            placeholder="Enter State"
            value={leadData.state}
            onChange={e => setleadData({ ...leadData, state: e.target.value })}
          />
        </Form.Item>
        <Form.Item
          className="w-[48%]"
          name="postalCode"
          // rules={[{ required: true, message: "Postal Code is required" }]}
          label="Postal Code"
        >
          <Input
            type="number"
            name="postalCode"
            placeholder="Enter Postal Code"
            value={leadData.postalCode}
            onChange={e => setleadData({ ...leadData, postalCode: e.target.value })}
          />
        </Form.Item>
        <Form.Item
          className="w-full"
          name="country"
          // rules={[{ required: true, message: "Country is required" }]}
          label="Country"
        >
          <Input
            name="country"
            placeholder="Enter Country"
            value={leadData.country}
            onChange={e => setleadData({ ...leadData, country: e.target.value })}
          />
        </Form.Item>
      </div>
    </div>
  );
}
